import { clsx } from '@alexis/helpers/clsx';
import { isEnvironmentProduction } from '@alexis/helpers/environment';
import { genzoTrackActionEvent } from '@alexis/helpers/genzo';
import cuid from 'cuid';
import type { FlightSearchType } from 'flights/flightSearchType';
import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';

import FlightSearchForm from '@components/flights/FlightSearchForm';
import CarRentalSkeleton from '@components/home/CarRentalSkeleton';
import HotelSearchForm from '@components/hotels/HotelSearchForm';

import { MEILI_PTID } from '@constants/general';
import { CAR_RENTAL_ALLOWED_LOCALES } from '@constants/general';

import {
  createMeiliDataWithWegoClickId,
  encodeMeiliAdditionalData,
} from '@helpers/meiliDataEncoder';
import { carRentalRoute, flightsRoute, hotelsRoute } from '@helpers/routeTranslation';

import ProductCarRentalIcon from '@icons/product-car-rental.svg';
import ProductFlightsIcon from '@icons/product-flights.svg';
import ProductHotelsIcon from '@icons/product-hotels.svg';
import ProductTrainIcon from '@icons/product-train.svg';

import useLocaleParam from '@hooks/useLocaleParam';
import useWeegioFeatureFlag from '@hooks/useWeegioFeatureFlag';

import {
  getCurrencyState,
  getCurrentSiteState,
  getLocaleState,
  getTranslationsState,
} from '@redux/selectors';

import styles from './SearchForm.module.scss';

const MEILI_LOAD_DELAY_TIME = 500;

export type SearchFormProps = {
  pageType: string;
  availablePaymentMethods: HomepageFlightsPaymentMethod[];
  geolocationCoordinates?: GeolocationCoordinates;
  nearestCityByIP?: Place;
  wegoAnalyticsClientId?: string;
  wegoAnalyticsClientSessionId?: string;
  pageViewId: string;
  isAncillaryServicesEnabled?: boolean;
};

const SearchForm: React.FC<SearchFormProps> = ({
  pageType,
  availablePaymentMethods,
  geolocationCoordinates,
  nearestCityByIP,
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
  pageViewId,
  isAncillaryServicesEnabled,
}) => {
  const tabsRef = useRef<HTMLDivElement>(null);
  const localeParam = useLocaleParam();
  const [isCarRentalLoading, setIsCarRentalLoading] = useState(true);

  const { search, state } = useLocation();

  const currentSite = useSelector(getCurrentSiteState);
  const { isWeegioHomepageBannerEnabled } = useWeegioFeatureFlag();
  const translations = useSelector(getTranslationsState);
  const [isMultiCitySelected, setIsMultiCitySelected] = useState<boolean>(false);
  const locale = useSelector(getLocaleState);
  const currency = useSelector(getCurrencyState);

  const searchWithoutExtraParams = useMemo<string>(() => {
    const searchParams = new URLSearchParams(search);

    searchParams.delete('open_calendar');

    const updatedSearchParams = searchParams.toString();

    return !!updatedSearchParams ? `?${updatedSearchParams}` : '';
  }, [search]);

  const trainsGenzoActionId = cuid();
  const [carRentalGenzoActionId, setCarRentalGenzoActionId] = useState(() => cuid());

  const isCarRentalPage = pageType === 'car-rentals';

  // Create Meili additional data with wego_click_id for car rentals
  const meiliDataQuery = useMemo<string | null>(() => {
    if (!isCarRentalPage) {
      return null;
    }
    try {
      const meiliData = createMeiliDataWithWegoClickId(carRentalGenzoActionId);

      return encodeMeiliAdditionalData(meiliData);
    } catch (error) {
      console.error('Error creating Meili additional data:', error);
      return null;
    }
  }, [isCarRentalPage, carRentalGenzoActionId]);

  const getTabIndex = (target: EventTarget) => {
    const tabs = tabsRef.current?.getElementsByClassName(styles.tab) ?? [];
    return Array.from(tabs).indexOf((target as Element).closest(`.${styles.tab}`) as Element);
  };

  const isINPOS = currentSite.countryCode === 'IN';

  const showCarRentalTab = CAR_RENTAL_ALLOWED_LOCALES.includes(locale) && !isINPOS;

  useEffect(() => {
    if (isCarRentalPage) {
      // Prevent multiple script loads
      const existingScript = document.querySelector('script[src*="search-ux.meili.travel"]');
      if (existingScript) {
        setIsCarRentalLoading(false);
        return;
      }

      setIsCarRentalLoading(true);

      // Validate the domain for security
      const allowedDomains = ['search-ux.meili.travel', 'uat-search-ux.meili.travel'];
      const domain = isEnvironmentProduction(window.location.hostname)
        ? 'search-ux.meili.travel'
        : 'uat-search-ux.meili.travel';

      if (!allowedDomains.includes(domain)) {
        console.error('Invalid script domain:', domain);
        setIsCarRentalLoading(false);
        return;
      }

      // Load the Meili script
      const script = document.createElement('script');
      script.src = `https://${domain}/index.js`;
      script.async = true;
      script.defer = false;

      let eventListenerCleanup: (() => void) | null = null;
      let timeoutId: NodeJS.Timeout | null = null;

      // Handle successful script load
      script.onload = () => {
        // Listen for Meili's ready event instead of arbitrary timeout
        const handleMeiliReady = (event: CustomEvent) => {
          if (event.detail?.appLoaded) {
            // Add a small delay before hiding the skeleton to ensure the Meili app is fully rendered
            setTimeout(() => {
              setIsCarRentalLoading(false);
              eventListenerCleanup?.();
            }, MEILI_LOAD_DELAY_TIME);
          }
        };

        eventListenerCleanup = () => {
          window.removeEventListener('MEILI_LOAD_LISTENER', handleMeiliReady as EventListener);
          if (timeoutId) clearTimeout(timeoutId);
        };

        window.addEventListener('MEILI_LOAD_LISTENER', handleMeiliReady as EventListener);

        // Fallback timeout in case event is not fired
        timeoutId = setTimeout(() => {
          setIsCarRentalLoading(false);
          eventListenerCleanup?.();
        }, MEILI_LOAD_DELAY_TIME * 4);
      };

      // Handle script load error
      script.onerror = () => {
        setIsCarRentalLoading(false);
        eventListenerCleanup?.();
      };

      document.body.appendChild(script);

      return () => {
        // Clean up the script when component unmounts
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
        eventListenerCleanup?.();
      };
    }
  }, [pageType]);

  // Effect to handle car rental search form submission tracking
  useEffect(() => {
    if (isCarRentalPage && wegoAnalyticsClientId && wegoAnalyticsClientSessionId) {
      // Track the event when hidden link is clicked
      const trackCarRentalSearch = () => {
        genzoTrackActionEvent(
          API_BASE_URL,
          wegoAnalyticsClientId,
          wegoAnalyticsClientSessionId,
          undefined,
          {
            id: pageViewId,
            category: 'products',
            object: 'car_rentals_search_form',
            action: 'clicked',
            value: carRentalGenzoActionId,
          },
          carRentalGenzoActionId,
        );
        // Update carRentalGenzoActionId after triggering the event
        setCarRentalGenzoActionId(cuid());
      };

      const handleCarRentalSubmit = (event: Event) => {
        const target = event.target as HTMLElement;

        // Check if the clicked element is the hidden link
        const isHiddenLink = target?.id === 'hidden-click-link';

        if (isHiddenLink) {
          trackCarRentalSearch();
        }
      };

      // Add event listener for click events on the document
      document.addEventListener('click', handleCarRentalSubmit, true); // Use capture phase

      return () => {
        // Clean up event listener
        document.removeEventListener('click', handleCarRentalSubmit, true);
      };
    }
  }, [
    isCarRentalPage,
    wegoAnalyticsClientId,
    wegoAnalyticsClientSessionId,
    pageViewId,
    carRentalGenzoActionId,
  ]);

  const handleOnProductsClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    eventData: {
      id?: string;
      category?: string;
      object?: string;
      action?: string;
      value: string;
    },
    genzoActionId?: string,
  ) => {
    if (!!wegoAnalyticsClientId && !!wegoAnalyticsClientSessionId) {
      genzoTrackActionEvent(
        API_BASE_URL,
        wegoAnalyticsClientId,
        wegoAnalyticsClientSessionId,
        undefined,
        {
          id: eventData.id || pageViewId,
          category: eventData.category || 'products',
          object: eventData.object || 'tab',
          action: eventData.action || (getTabIndex(e.target) + 1).toString(),
          value: eventData.value,
        },
        genzoActionId,
      );
    }
  };

  const handleSearchTypeChange = useCallback((searchType: FlightSearchType) => {
    setIsMultiCitySelected(searchType === 'multiCity');
  }, []);
  const handleWeegioBannerClicked = (): void => {
    const targetUrl = locale === 'ar' ? 'https://company.wego.com/ar' : 'https://company.wego.com/';
    window.location.href = targetUrl;
  };

  return (
    <div
      className={clsx(
        styles.searchForm,
        styles.formMinHeight,
        isMultiCitySelected &&
          isAncillaryServicesEnabled &&
          styles.marginBottomForAncillaryServices,
        !isAncillaryServicesEnabled && !isCarRentalPage && styles.hideAncillaryServices,
      )}
    >
      {isWeegioHomepageBannerEnabled ? (
        <div className={styles.weegioContainer} onClick={() => handleWeegioBannerClicked()}>
          <img
            src='https://zen.wego.com/cdn-cgi/image/format=auto,quality=95/cms/images/weegio_354916968.png'
            alt='Weegio'
          />
        </div>
      ) : null}

      <div ref={tabsRef} className={styles.tabs}>
        <div className={styles.productTabs}>
          <NavLink
            className={({ isActive }) =>
              clsx(styles.tab, (pageType === 'home' || isActive) && styles.active)
            }
            to={{
              pathname: flightsRoute(currentSite, localeParam),
              search: searchWithoutExtraParams,
            }}
            state={state}
            onClick={(e) =>
              handleOnProductsClick(e, {
                value: 'flights',
              })
            }
          >
            <ProductFlightsIcon className={styles.iconSvg} />
            {translations.flights}
          </NavLink>

          <NavLink
            className={({ isActive }) => clsx(styles.tab, isActive && styles.active)}
            to={{
              pathname: hotelsRoute(currentSite, localeParam),
              search: searchWithoutExtraParams,
            }}
            state={state}
            onClick={(e) =>
              handleOnProductsClick(e, {
                value: 'hotels',
              })
            }
          >
            <ProductHotelsIcon className={styles.iconSvg} />
            {translations.hotels}
          </NavLink>
          {showCarRentalTab && (
            <NavLink
              className={({ isActive }) => clsx(styles.tab, isActive && styles.active)}
              to={{
                pathname: carRentalRoute(localeParam),
                search: searchWithoutExtraParams,
              }}
              state={state}
              onClick={(e) =>
                handleOnProductsClick(e, {
                  value: 'car_rentals',
                  action: '5',
                })
              }
            >
              <ProductCarRentalIcon className={styles.iconSvg} />
              {translations.car_rentals}
            </NavLink>
          )}

          {isINPOS ? (
            <a
              className={styles.tab}
              href={`https://trains.wego.com?wego_click_id=${trainsGenzoActionId}`}
              target='_blank'
              rel='noreferrer nofollow'
              onClick={(e) =>
                handleOnProductsClick(
                  e,
                  {
                    value: 'trains',
                  },
                  trainsGenzoActionId,
                )
              }
            >
              <ProductTrainIcon className={styles.iconSvg} />
              {translations.wego_trains}
            </a>
          ) : null}
        </div>
      </div>

      <div className={styles.tabContent}>
        {/^(?:home|flights)$/i.test(pageType) ? (
          <FlightSearchForm
            geolocationCoordinates={geolocationCoordinates}
            nearestCityByIP={nearestCityByIP}
            paymentMethods={availablePaymentMethods}
            pageViewId={pageViewId}
            wegoAnalyticsClientId={wegoAnalyticsClientId}
            wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
            onSearchTypeChange={handleSearchTypeChange}
          />
        ) : null}

        {pageType === 'hotels' ? (
          <HotelSearchForm
            geolocationCoordinates={geolocationCoordinates}
            localeParam={localeParam}
            nearestCityByIP={nearestCityByIP}
            wegoAnalyticsClientId={wegoAnalyticsClientId}
            wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
            pageViewId={pageViewId}
          />
        ) : null}

        {isCarRentalPage ? (
          <div className={styles.carRentalContainer}>
            <CarRentalSkeleton className={!isCarRentalLoading ? styles.skeletonHidden : ''} />
            <div
              id='mli-root'
              className={clsx('mli-loading', styles.meiliContainer)}
              data-ptid={MEILI_PTID}
              data-currency={currency.code}
              data-locale={locale === 'ar' ? 'ar-EG' : 'en-US'}
              data-params={JSON.stringify({ wego_click_id: carRentalGenzoActionId })}
              data-query={meiliDataQuery || ''}
              style={{ padding: '0px' }}
              data-direction={locale === 'ar' ? 'rtl' : 'ltr'}
            ></div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SearchForm;
